import { useEffect, useMemo, useState } from 'react';

import {
  THEME_CHANGE_EVENT,
  THEME_STORAGE_KEY,
  ThemePreference,
  getStoredThemePreference,
  getSystemThemeMode,
  resolveThemeMode,
  setStoredThemePreference,
  type ResolvedThemeMode,
} from 'theme';

export function useThemePreference() {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(() => getStoredThemePreference());
  const [systemThemeMode, setSystemThemeMode] = useState<ResolvedThemeMode>(() => getSystemThemeMode());

  useEffect(() => {
    const syncThemePreference = () => {
      setThemePreferenceState(getStoredThemePreference());
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY) {
        syncThemePreference();
      }
    };

    const handleThemeChange = (event: Event) => {
      const custom = event as CustomEvent<{ mode?: ThemePreference }>;
      if (custom.detail?.mode) {
        setThemePreferenceState(custom.detail.mode);
      }
    };

    const handleFocus = () => {
      syncThemePreference();
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener(THEME_CHANGE_EVENT, handleThemeChange as EventListener);
    window.addEventListener('focus', handleFocus);
    syncThemePreference();

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(THEME_CHANGE_EVENT, handleThemeChange as EventListener);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    if (themePreference !== 'system') {
      return;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const syncSystemThemeMode = () => {
      setSystemThemeMode(media.matches ? 'dark' : 'light');
    };

    syncSystemThemeMode();

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', syncSystemThemeMode);
      return () => media.removeEventListener('change', syncSystemThemeMode);
    }

    media.addListener(syncSystemThemeMode);
    return () => media.removeListener(syncSystemThemeMode);
  }, [themePreference]);

  const resolvedThemeMode = useMemo(
    () => resolveThemeMode(themePreference, systemThemeMode),
    [systemThemeMode, themePreference],
  );

  const setThemePreference = (nextPreference: ThemePreference): void => {
    setStoredThemePreference(nextPreference);
    setThemePreferenceState(nextPreference);
    window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: { mode: nextPreference } }));
  };

  return {
    resolvedThemeMode,
    setThemePreference,
    themePreference,
  };
}
